import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Mail, MessageCircle } from 'lucide-react';
import { Botao, Cartao, Selo, classesBotao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Alternador, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, textoDeErroGravado } from '../../nucleo/api';
import { dataHora, email, nomeParceiro, telefoneFormatado } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { AntecedenciaAvisos } from '../../nucleo/notificacoes/AntecedenciaAvisos';
import { useContasFin } from './dados';
import { ProvedorConfig } from './ProvedorCobranca';
import { FORMAS_COBRANCA, invalidarContas, rotuloEtapaEnvio, type ConfigAsaas, type Envio, type FormaCobranca, type Regua } from './tipos';

/** M5.4: registro de cada envio da régua de cobrança e configuração da régua (`contas.regua`). */
export function Cobrancas() {
  const { t } = useTranslation('contas');
  const s = useSessao();
  const colunas: Coluna<Envio>[] = [
    { chave: 'enviado_em', rotulo: t('cobrancas.enviadoEm'), mono: true, render: (e) => dataHora(e.enviado_em) },
    {
      chave: 'documento_numero',
      rotulo: t('cobrancas.documento'),
      principal: true,
      render: (e) => (
        <span className="flex min-w-0 flex-col">
          <span className="numero truncate font-medium" title={e.documento_numero}>{e.documento_numero}</span>
          <span className="truncate text-xs text-tinta-3" title={nomeParceiro(e.parceiro_nome)}>{nomeParceiro(e.parceiro_nome)}</span>
        </span>
      ),
    },
    {
      chave: 'canal',
      rotulo: t('cobrancas.canal'),
      render: (e) => (
        <span className="inline-flex items-center gap-2">
          {e.canal === 'email' ? <Mail className="size-4 text-tinta-3" aria-hidden /> : <MessageCircle className="size-4 text-tinta-3" aria-hidden />}
          {t(`canal.${e.canal}`)}
        </span>
      ),
    },
    { chave: 'etapa', rotulo: t('cobrancas.etapa'), render: (e) => t(...rotuloEtapaEnvio(e)) },
    // Review r19 spec (M1): e-mail ou celular do cliente pelo formatador do modo discreto (a W29 grava cada envio manual aqui).
    { chave: 'destino', rotulo: t('cobrancas.destino'), oculta: true, render: (e) => (!e.destino ? '' : e.canal === 'email' ? email(e.destino) : telefoneFormatado(e.destino)) },
    {
      chave: 'resultado',
      rotulo: t('cobrancas.resultado'),
      render: (e) => (
        <span className="flex min-w-0 flex-col items-start gap-1">
          <Selo cor={e.resultado === 'enviado' ? 'verde' : e.resultado === 'falhou' ? 'perigo' : 'neutro'}>{t(`resultado.${e.resultado}`)}</Selo>
          {e.erro && <span className="max-w-64 truncate text-xs text-tinta-3" title={textoDeErroGravado(e.erro)}>{textoDeErroGravado(e.erro)}</span>}
        </span>
      ),
    },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <ReguaConfig />
      {/* Fase 2 (W28): cobrança pelo Asaas (automática na emissão, forma e conta das baixas). */}
      {s.instalado('integracoes') && <AsaasConfig />}
      {/* Trilha 2 (Pix Sicredi): qual banco cobra cada fatura (workspace e empresa; cliente e contrato nas fichas). */}
      {s.instalado('integracoes') && <ProvedorConfig />}
      {/* Fase 2 (W22, K8): antecedência dos avisos de vencimento (contas a pagar, faturas e contratos), do workspace. */}
      <AntecedenciaAvisos />
      <Tabela<Envio> tela="contas.cobrancas" caminho="/contas/cobrancas" colunas={colunas} vazio={
          <Vazio
            texto={t('vazio.cobrancas')}
            acao={
              <Link to="/financeiro/contas/a-receber" className={classesBotao()}>
                {t('vazio.verAReceber')}
              </Link>
            }
          />
        }
      />
    </div>
  );
}

/** Dias da régua como texto "-3, 0, 3, 7, 15" (negativo é antes do vencimento). */
const paraTexto = (dias: number[]) => dias.join(', ');
function paraDias(texto: string): number[] | null {
  const partes = texto.split(/[,;\s]+/).filter(Boolean);
  const dias = partes.map(Number);
  if (dias.some((d) => !Number.isInteger(d) || d < -60 || d > 365) || dias.length > 20) return null;
  return [...new Set(dias)].sort((a, b) => a - b);
}

function ReguaConfig() {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/contas/regua'], queryFn: () => get<Regua>('/contas/regua') });
  const [texto, setTexto] = useState('');
  const [email, setEmail] = useState(true);
  const [whatsapp, setWhatsapp] = useState(true);
  const [ativa, setAtiva] = useState(true);
  useEffect(() => {
    if (!q.data) return;
    setTexto(paraTexto(q.data.dias));
    setEmail(q.data.canais.includes('email'));
    setWhatsapp(q.data.canais.includes('whatsapp'));
    setAtiva(q.data.ativa);
  }, [q.data]);
  const dias = paraDias(texto);
  // r9 robustez: régua sem nenhum dia é recusada pelo servidor (`regua.sem_dias`); a tela já não deixa salvar.
  const semDias = !!dias && dias.length === 0;
  const erroDias = !dias ? t('regua.diasInvalidos') : semDias ? t('regua.semDias') : undefined;
  // Sem Conversas ou sem conector conectado a função aparece desativada, com o motivo e o caminho para resolver (a preferência gravada não muda).
  const whatsappLivre = q.data?.whatsapp_disponivel ?? true;
  const motivo = q.data?.whatsapp_indisponivel_motivo;
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/contas/regua', { dias, canais: [...(email ? ['email'] : []), ...(whatsapp ? ['whatsapp'] : [])], ativa }),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const podeEditar = podeNoWorkspace(s, 'contas.configurar');

  return (
    <Cartao titulo={t('regua.titulo')}>
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <p className="text-sm text-tinta-3 md:col-span-3">{t('regua.explicacao')}</p>
          <div className="md:col-span-2">
            <Rotulo id="regua-dias" rotulo={t('regua.dias')} erro={erroDias} ajuda={t('regua.diasAjuda')}>
              {/* r10 visual B8: o erro (ou a ajuda) fica ligado ao campo, e o leitor o ouve ao chegar nele. */}
              <Entrada id="regua-dias" value={texto} disabled={!podeEditar} aria-invalid={erroDias ? true : undefined} aria-describedby={erroDias ? 'regua-dias-erro' : 'regua-dias-ajuda'} onChange={(e) => setTexto(e.target.value)} className="numero" />
            </Rotulo>
          </div>
          <div className="flex min-w-0 flex-col gap-3">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('regua.ativa')}</span>
              <Alternador marcado={ativa} aoMudar={setAtiva} rotulo={t('regua.ativa')} desabilitado={!podeEditar} />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('canal.email')}</span>
              <Alternador marcado={email} aoMudar={setEmail} rotulo={t('canal.email')} desabilitado={!podeEditar} />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('canal.whatsapp')}</span>
              <Alternador marcado={whatsapp && whatsappLivre} aoMudar={setWhatsapp} rotulo={t('canal.whatsapp')} desabilitado={!podeEditar || !whatsappLivre} />
            </label>
            {!whatsappLivre && (
              <p className="text-xs text-tinta-3" role="status">
                {t(motivo === 'modulo' ? 'regua.whatsappSemModulo' : 'regua.whatsappSemConector')}{' '}
                {motivo === 'modulo' ? (
                  s.pode('admin.modulos') && (
                    <Link to="/admin/admin/modulos" className="-my-1 inline-block py-1 font-medium text-acento-forte hover:underline">
                      {t('regua.instalarModulo')}
                    </Link>
                  )
                ) : (
                  <Link to="/integracoes/integracoes" className="-my-1 inline-block py-1 font-medium text-acento-forte hover:underline">
                    {t('regua.conectarWhatsapp')}
                  </Link>
                )}
              </p>
            )}
          </div>
          {podeEditar && (
            <div className="md:col-span-3">
              <Botao variante="secundario" disabled={!!erroDias} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {tn('comum.salvar')}
              </Botao>
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}

/**
 * Fase 2 (W28): configuração da cobrança pelo Asaas (`contas.asaas`). Mesmo cartão e controles da régua: liga a cobrança
 * automática na emissão, escolhe a forma e a conta do Financeiro onde caem as baixas que chegam pelo webhook.
 */
function AsaasConfig() {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/contas/asaas'], queryFn: () => get<ConfigAsaas>('/contas/asaas') });
  const contas = useContasFin();
  const [automatica, setAutomatica] = useState(false);
  const [forma, setForma] = useState<FormaCobranca>('indefinida');
  const [contaId, setContaId] = useState('');
  useEffect(() => {
    if (!q.data) return;
    setAutomatica(q.data.automatica);
    setForma(q.data.forma);
    setContaId(q.data.conta_id ?? '');
  }, [q.data]);
  const salvar = useMutation({
    mutationFn: () => api<{ aviso?: string | null }>('PUT', '/contas/asaas', { automatica, forma, conta_id: contaId || null }),
    onSuccess: async (r) => {
      await invalidarContas(qc);
      toast.sucesso(tn('comum.salvo'));
      // Review r18 segurança (M1): a conta é de uma empresa; documento de outra empresa baixa na conta padrão dela.
      if (r?.aviso) toast.info(t('asaas.contaOutrasEmpresas'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const podeEditar = podeNoWorkspace(s, 'contas.configurar');
  const opcoesConta = (contas.data?.itens ?? []).filter((c) => c.moeda === 'BRL').map((c) => ({ valor: c.id, rotulo: c.nome }));

  return (
    <Cartao titulo={t('asaas.titulo')}>
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-asaas-config>
          <p className="text-sm text-tinta-3 md:col-span-3">
            {t('asaas.explicacao')}{' '}
            {q.data.conectado ? (
              <Selo cor={q.data.ambiente === 'producao' ? 'verde' : 'neutro'}>{t(`asaas.ambiente.${q.data.ambiente ?? 'sandbox'}`)}</Selo>
            ) : (
              <Link to="/integracoes/integracoes/conectores" className="-my-1 inline-block py-1 font-medium text-acento-forte hover:underline">
                {t('asaas.conectar')}
              </Link>
            )}
          </p>
          <div className="flex min-w-0 flex-col gap-3">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('asaas.automatica')}</span>
              <Alternador marcado={automatica} aoMudar={setAutomatica} rotulo={t('asaas.automatica')} desabilitado={!podeEditar} />
            </label>
            <p className="text-xs text-tinta-3">{t('asaas.automaticaAjuda')}</p>
          </div>
          <Rotulo id="asaas-forma" rotulo={t('asaas.forma')} ajuda={t(`cobranca.formaAjuda.${forma}`)}>
            <Selecao id="asaas-forma" aria-describedby="asaas-forma-ajuda" disabled={!podeEditar} value={forma} onChange={(e) => setForma(e.target.value as FormaCobranca)} opcoes={FORMAS_COBRANCA.map((f) => ({ valor: f, rotulo: t(`cobranca.forma.${f}`) }))} />
          </Rotulo>
          <Rotulo id="asaas-conta" rotulo={t('asaas.conta')} ajuda={t('asaas.contaAjuda')}>
            <Selecao id="asaas-conta" aria-describedby="asaas-conta-ajuda" disabled={!podeEditar} value={contaId} onChange={(e) => setContaId(e.target.value)} vazio={t('asaas.contaPadrao')} opcoes={opcoesConta} />
          </Rotulo>
          {podeEditar && (
            <div className="md:col-span-3">
              <Botao variante="secundario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {tn('comum.salvar')}
              </Botao>
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}
